import React from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { NewChatPicker } from '../chat/NewChatPicker';
import { PersonaAvatar } from '../personas/PersonaAvatar';
import { usePersonas } from '../../hooks/usePersonas';
import { getTierPresentation } from '../../utils/personaTier';

interface PersonaSelectorProps {
  selectedPersonaId: string;
  onSelectPersona: (personaId: string) => void;
}

/**
 * Controlled persona-assign dropdown (NodeInspector blueprint nodes). Thin
 * wrapper over NewChatPicker variant='select' — same shape as InvitePeerHost.
 * The trigger resolves selectedPersonaId → Persona via the shared usePersonas
 * cache; the panel (portal-to-body) owns search / keyboard a11y / loading.
 */
const PersonaSelector: React.FC<PersonaSelectorProps> = ({ selectedPersonaId, onSelectPersona }) => {
  const { data: personas } = usePersonas();
  const selected = personas?.find(p => p.id === selectedPersonaId) ?? null;

  return (
    <NewChatPicker
      variant="select"
      onSelect={(p) => onSelectPersona(p.id)}
      trigger={({ open, isOpen }) => (
        <button
          type="button"
          data-testid="persona-selector-trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={open}
          className="w-full flex items-center gap-2 rounded-md border px-2 py-2 text-sm text-left
                     bg-white border-gray-300 text-gray-900
                     dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100
                     hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <PersonaAvatar
                name={selected.name}
                avatarUrl={selected.avatarUrl}
                specialization={selected.primarySpecialization}
                size="small"
              />
              <span aria-hidden="true">{getTierPresentation(selected).glyph}</span>
              <span className="sr-only">{getTierPresentation(selected).ariaLabel}</span>
              <span className="flex flex-col min-w-0">
                <span className="font-medium truncate">{selected.name}</span>
                <span className="text-xs truncate text-gray-500 dark:text-gray-400">
                  {selected.primarySpecialization || selected.shortDescription || ''}
                </span>
              </span>
            </span>
          ) : (
            // While usePersonas is in-flight or the id is stale, show the id itself
            // (informative) rather than a synthetic fake persona or a bare placeholder.
            <span className="flex-1 text-gray-400 dark:text-gray-500">
              {selectedPersonaId || 'Select a persona…'}
            </span>
          )}
          <FiChevronDown className="shrink-0 text-gray-400" aria-hidden="true" />
        </button>
      )}
    />
  );
};

export default PersonaSelector;
